import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import type React from 'react';
import {
  INSURANCE_COMPANIES_MAP,
  type InsuranceBrokerCredential,
  type InsuranceCompany,
} from 'services/types/businessUnit';
import { NAVY, RADIUS_ITEM, SURFACE_CONTAINER_LOW, SURFACE_CONTAINER_LOWEST } from 'styles/tokens';

interface InsuranceCredentialsListProps {
  credentials: InsuranceBrokerCredential[];
  selectedCompany: InsuranceCompany | null;
  onCompanySelect: (company: InsuranceCompany) => void;
}

/**
 * Component for displaying a list of insurance companies with their configuration status
 *
 * @param credentials - Array of current credentials
 * @param selectedCompany - Currently selected company
 * @param onCompanySelect - Callback when a company is selected
 * @returns React component for the insurance companies list
 */
const InsuranceCredentialsList: React.FC<InsuranceCredentialsListProps> = ({
  credentials,
  selectedCompany,
  onCompanySelect,
}) => {
  /**
   * Generates the logo path for an insurance company
   *
   * @param company - Name of the insurance company
   * @returns Path to the company logo
   */
  const getLogoPath = (company: InsuranceCompany) => {
    const logoName = company
      .toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9-]/g, '');
    return `/logos/insurance/${logoName}.png`;
  };

  /**
   * Gets the credential object for a specific company
   *
   * @param company - Name of the insurance company
   * @returns Credential object or undefined if not found
   */
  const getCredentialForCompany = (company: InsuranceCompany) => {
    return credentials.find((c) => c.company === company);
  };

  /**
   * Checks if a company has configured credentials
   *
   * @param company - Name of the insurance company
   * @returns True if the company has credentials configured
   */
  const hasCredentials = (company: InsuranceCompany) => {
    const credential = getCredentialForCompany(company);
    return credential && (credential.username || credential.password);
  };

  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Desktop: vertical label */}
      <Typography
        variant="h6"
        sx={{
          p: 2.5,
          pb: 1.5,
          flexShrink: 0,
          fontFamily: 'Jost',
          fontWeight: 600,
          color: NAVY,
          letterSpacing: '0.05em',
          textTransform: 'uppercase',
          fontSize: '0.75rem',
          display: { xs: 'none', md: 'block' },
        }}
      >
        Compañías de Seguros
      </Typography>

      {/* Desktop: vertical list */}
      <List sx={{ p: 0, flex: 1, overflow: 'auto', display: { xs: 'none', md: 'block' } }}>
        {Object.keys(INSURANCE_COMPANIES_MAP).map((company) => {
          const companyKey = company as InsuranceCompany;
          const isSelected = selectedCompany === companyKey;
          const isConfigured = hasCredentials(companyKey);
          const credential = getCredentialForCompany(companyKey);

          return (
            <ListItem key={companyKey} disablePadding>
              <ListItemButton
                selected={isSelected}
                onClick={() => onCompanySelect(companyKey)}
                sx={{
                  borderRadius: RADIUS_ITEM,
                  mx: 1,
                  mb: 0.5,
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: SURFACE_CONTAINER_LOW,
                  },
                  '&.Mui-selected': {
                    backgroundColor: NAVY,
                    color: '#ffffff',
                    '&:hover': {
                      backgroundColor: NAVY,
                      opacity: 0.95,
                    },
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 56 }}>
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: RADIUS_ITEM,
                      backgroundColor: SURFACE_CONTAINER_LOWEST,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <img
                      src={getLogoPath(companyKey)}
                      alt={companyKey}
                      style={{
                        width: 28,
                        height: 28,
                        objectFit: 'contain',
                      }}
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  </Box>
                </ListItemIcon>
                <ListItemText
                  primary={INSURANCE_COMPANIES_MAP[companyKey]}
                  slotProps={{
                    primary: {
                      variant: 'body2',
                      sx: {
                        fontFamily: 'Jost',
                        fontWeight: isSelected ? 600 : 400,
                        color: isSelected ? '#ffffff' : NAVY,
                      },
                    },
                  }}
                />
                {isConfigured && (
                  <>
                    {credential?.discount != null && credential.discount > 0 && (
                      <Chip
                        label={`${Math.round(credential.discount * 100)}%`}
                        size="small"
                        sx={{
                          ml: 0.5,
                          fontSize: '0.65rem',
                          fontFamily: 'Jost',
                          fontWeight: 600,
                          height: 20,
                          backgroundColor: isSelected ? 'rgba(255,255,255,0.2)' : NAVY,
                          color: '#ffffff',
                          border: 'none',
                          '& .MuiChip-label': { px: 1 },
                        }}
                      />
                    )}
                    <Chip
                      label="Configurado"
                      size="small"
                      sx={{
                        ml: 0.5,
                        fontSize: '0.65rem',
                        fontFamily: 'Jost',
                        fontWeight: 600,
                        height: 20,
                        backgroundColor: isSelected ? 'rgba(255,255,255,0.2)' : 'rgba(37,97,111,0.12)',
                        color: isSelected ? '#ffffff' : NAVY,
                        border: 'none',
                        '& .MuiChip-label': { px: 1 },
                      }}
                    />
                  </>
                )}
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>

      {/* Mobile: horizontal scroll strip */}
      <Box
        sx={{
          display: { xs: 'flex', md: 'none' },
          overflowX: 'auto',
          gap: 1,
          p: 1,
          '&::-webkit-scrollbar': { display: 'none' },
          scrollbarWidth: 'none',
        }}
      >
        {Object.keys(INSURANCE_COMPANIES_MAP).map((company) => {
          const companyKey = company as InsuranceCompany;
          const isSelected = selectedCompany === companyKey;
          const isConfigured = hasCredentials(companyKey);

          return (
            <Box
              key={companyKey}
              onClick={() => onCompanySelect(companyKey)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1.5,
                py: 1,
                borderRadius: RADIUS_ITEM,
                backgroundColor: isSelected ? NAVY : 'transparent',
                cursor: 'pointer',
                flexShrink: 0,
                transition: 'all 0.2s ease',
              }}
            >
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: '6px',
                  backgroundColor: SURFACE_CONTAINER_LOWEST,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <img
                  src={getLogoPath(companyKey)}
                  alt={companyKey}
                  style={{ width: 22, height: 22, objectFit: 'contain' }}
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
              </Box>
              <Typography
                variant="caption"
                sx={{
                  fontFamily: 'Jost',
                  fontWeight: isSelected ? 600 : 500,
                  color: isSelected ? '#ffffff' : NAVY,
                  whiteSpace: 'nowrap',
                  fontSize: '0.75rem',
                }}
              >
                {INSURANCE_COMPANIES_MAP[companyKey]}
              </Typography>
              {isConfigured && (
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    backgroundColor: isSelected ? '#ffffff' : NAVY,
                    flexShrink: 0,
                  }}
                />
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default InsuranceCredentialsList;
